import React, { useContext, useEffect, useState } from "react";
import { Box, Flex, Spinner, Badge } from "theme-ui";
import {
  getInvoicesForPO,
  approveProviderInvoice,
  rejectProviderInvoice,
  getProviderInvoiceFileUrl,
} from "api/ProviderInvoice";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import TableBasic from "components/ui/Table";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import { themevals } from "theme/themevals";
import moment from "moment";
import SubmitInvoice from "./SubmitInvoice";
import UserContext from "context/User";
import { PlusIcon } from "components/svg";
import { createColumnHelper } from "@tanstack/react-table";
import { useToast } from "components/toast";

interface InvoicesListProps {
  po: any;
  reloadPO: () => void;
  /** Set once the provider has acknowledged the PO terms, either in an
   *  earlier session (on `po`) or in this one (held in view.tsx state). */
  termsAcknowledgedAt?: string | null;
  /** Opens the acknowledgment modal. See the submit handler below. */
  onRequireAcknowledgment?: () => void;
}

const InvoicesList: React.FC<InvoicesListProps> = ({
  po,
  reloadPO,
  termsAcknowledgedAt,
  onRequireAcknowledgment,
}) => {
  const { user } = useContext<any>(UserContext);
  const toast = useToast();
  const isProvider = user?.role === null;
  const isCompany = user?.accessLevel === "isCompany";
  const [invoices, setInvoices] = useState<any[] | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);

  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");

  // Approve confirmation modal (zero-variance)
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState<any | null>(null);

  // Variance confirmation modal
  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [varianceTarget, setVarianceTarget] = useState<any | null>(null);

  // File preview modal
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [filePreviewName, setFilePreviewName] = useState<string>("");
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);

  const columnHelper = createColumnHelper();

  // Editable fields for email-sourced invoices (ref to avoid focus loss)
  const editedFieldsRef = React.useRef<Record<string, { invoiceNumber?: string; amount?: string }>>({});

  const isEmailSource = (inv: any) => inv?.source === "email";

  const getEditedInvoiceNumber = (inv: any) =>
    editedFieldsRef.current[inv.id]?.invoiceNumber ?? inv.invoiceNumber ?? "";

  const getEditedAmount = (inv: any) =>
    editedFieldsRef.current[inv.id]?.amount ?? (inv.amount != null ? String(inv.amount) : "");

  const updateEditedField = (invoiceId: string, field: "invoiceNumber" | "amount", value: string) => {
    editedFieldsRef.current = {
      ...editedFieldsRef.current,
      [invoiceId]: { ...editedFieldsRef.current[invoiceId], [field]: value },
    };
  };

  const canApproveEmailInvoice = (inv: any) => {
    if (!isEmailSource(inv)) return true;
    const num = getEditedInvoiceNumber(inv);
    const amt = getEditedAmount(inv);
    return num.trim().length > 0 && amt.trim().length > 0 && !isNaN(Number(amt));
  };

  // Send whatever staff typed when it differs from what is stored (fill in
  // a blank OR correct an AI misread). The server logs a correction.
  const getApproveOptions = (inv: any) => {
    const opts: any = {};
    const editedNum = editedFieldsRef.current[inv.id]?.invoiceNumber?.trim();
    const editedAmt = editedFieldsRef.current[inv.id]?.amount?.trim();
    if (editedNum && editedNum !== String(inv.invoiceNumber ?? "").trim()) opts.invoiceNumber = editedNum;
    if (editedAmt && !isNaN(Number(editedAmt)) && Number(editedAmt) !== Number(inv.amount ?? NaN)) opts.amount = Number(editedAmt);
    return opts;
  };

  const loadInvoices = async () => {
    try {
      const response = await getInvoicesForPO(po.id);
      if (response.success) {
        setInvoices(response.data || []);
      } else {
        setInvoices([]);
      }
    } catch (e) {
      console.error("Failed to load invoices", e);
      setInvoices([]);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [po.id]);

  const handleSubmitSuccess = () => {
    setShowSubmitModal(false);
    loadInvoices();
    reloadPO();
  };

  // NO STATUS FLOOR FOR ANYONE (Jack, Session 76). Previously `>= 2`,
  // which hid this button on the large majority of records — production
  // holds 564 POs at status 0 and 27 at status 1 — so an invoice for real
  // work had nowhere to land. Staff submit on behalf of legacy providers
  // who email invoices to billing@; providers invoice work they have
  // done. The uploader identity is stored on the record, so
  // on-behalf-of uploads stay distinguishable from self-uploads.
  //
  // Repeat and revised invoices are safe by construction: the backend
  // routes a same-number-different-amount submission to
  // `revision_pending`, treats a submission against an already-billed PO
  // as a revision, marks the prior invoice `superseded` with
  // `supersededById` on approval, and sends duplicates to
  // `duplicate_ignored`. Nothing is silently overwritten.
  const canSubmitInvoice = isProvider || isCompany;

  /**
   * Acceptance used to be the only thing that forced a provider through
   * the terms acknowledgment. With the status floor gone, a provider can
   * reach invoicing without ever accepting — so the acknowledgment moves
   * here rather than disappearing. Terms still get read before money
   * moves; only the moment changed.
   *
   * Staff are exempt: they are not the party agreeing to the terms.
   */
  const openSubmitInvoice = () => {
    const acknowledged = po?.termsAcknowledgedAt || termsAcknowledgedAt;
    if (isProvider && !acknowledged && typeof onRequireAcknowledgment === "function") {
      onRequireAcknowledgment();
      return;
    }
    setShowSubmitModal(true);
  };

  // Allow submission if no invoices, or only rejected/duplicate_ignored/superseded invoices exist,
  // OR if the only active invoice is bill_created (revision case)
  const hasBlockingInvoice =
    invoices &&
    invoices.some(
      (inv: any) =>
        inv.status !== "rejected" &&
        inv.status !== "duplicate_ignored" &&
        inv.status !== "superseded" &&
        inv.status !== "bill_created"
    );

  const isPendingStatus = (status: string) =>
    status === "pending_approval" || status === "review_needed" || status === "revision_pending";

  // --- Approve flow ---
  const handleApprove = async (invoice: any, approveWithVariance?: boolean) => {
    if (!approveWithVariance) {
      if (!canApproveEmailInvoice(invoice)) {
        toast.add("Please fill in Invoice # and Amount before approving.", themevals.colors.danger);
        return;
      }
      const pct = Math.abs(Number(invoice.variancePct || 0));
      if (pct > 5) {
        setVarianceTarget(invoice);
        setShowVarianceModal(true);
        return;
      }
      // Zero/low variance — show simple confirm
      setApproveTarget(invoice);
      setShowApproveModal(true);
      return;
    }

    // Actually call the API
    setProcessing(invoice.id);
    try {
      const response = await approveProviderInvoice(invoice.id, {
        approveWithVariance: approveWithVariance || false,
        ...getApproveOptions(invoice),
      });

      if (response.error === "variance_requires_confirmation") {
        setVarianceTarget({
          ...invoice,
          amount: response.data?.amount ?? invoice.amount,
          varianceAmount: response.data?.varianceAmount ?? invoice.varianceAmount,
          variancePct: response.data?.variancePct ?? invoice.variancePct,
        });
        setShowVarianceModal(true);
        setProcessing(null);
        return;
      }

      if (response.success) {
        toast.add(
          `Invoice approved! Bill ${response.billId || ""} created in Zoho.`,
          themevals.colors.success
        );
        setShowVarianceModal(false);
        setVarianceTarget(null);
        loadInvoices();
        reloadPO();
      } else {
        toast.add(response.error || "Failed to approve invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "Error approving invoice", themevals.colors.danger);
    }
    setProcessing(null);
  };

  const handleConfirmApprove = async () => {
    if (!approveTarget) return;
    setShowApproveModal(false);
    setProcessing(approveTarget.id);
    try {
      const response = await approveProviderInvoice(approveTarget.id, {
        approveWithVariance: false,
        ...getApproveOptions(approveTarget),
      });

      if (response.error === "variance_requires_confirmation") {
        setVarianceTarget({
          ...approveTarget,
          amount: response.data?.amount ?? approveTarget.amount,
          varianceAmount: response.data?.varianceAmount ?? approveTarget.varianceAmount,
          variancePct: response.data?.variancePct ?? approveTarget.variancePct,
        });
        setShowVarianceModal(true);
        setProcessing(null);
        setApproveTarget(null);
        return;
      }

      if (response.success) {
        toast.add(
          `Invoice approved! Bill ${response.billId || ""} created in Zoho.`,
          themevals.colors.success
        );
        loadInvoices();
        reloadPO();
      } else {
        toast.add(response.error || "Failed to approve invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "Error approving invoice", themevals.colors.danger);
    }
    setProcessing(null);
    setApproveTarget(null);
  };

  const handleApproveWithVariance = async () => {
    if (!varianceTarget) return;
    await handleApprove(varianceTarget, true);
  };

  // --- Reject flow ---
  const openRejectModal = (invoice: any) => {
    setRejectTarget(invoice);
    setRejectNotes("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setProcessing(rejectTarget.id);
    try {
      const response = await rejectProviderInvoice(rejectTarget.id, rejectNotes);
      if (response.success) {
        toast.add("Invoice rejected.", themevals.colors.success);
        setShowRejectModal(false);
        setRejectTarget(null);
        loadInvoices();
        reloadPO();
      } else {
        toast.add(response.error || "Failed to reject invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "Error rejecting invoice", themevals.colors.danger);
    }
    setProcessing(null);
  };

  // --- File preview ---
  const openFilePreview = async (invoice: any) => {
    if (!invoice.id) return;
    setFilePreviewName(invoice.fileName || "Invoice File");
    setFilePreviewLoading(true);
    setShowFilePreview(true);
    setFilePreviewUrl(null);
    try {
      const res = await getProviderInvoiceFileUrl(invoice.id);
      if (res.success && res.url) {
        setFilePreviewUrl(res.url);
      } else {
        toast.add(res.error || "Unable to load file", themevals.colors.danger);
        setShowFilePreview(false);
      }
    } catch (e: any) {
      toast.add("Error loading file preview", themevals.colors.danger);
      setShowFilePreview(false);
    }
    setFilePreviewLoading(false);
  };

  const isImageFile = (name: string) => {
    const ext = name.toLowerCase().split(".").pop() || "";
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "pending_approval":
        return <Badge as="span" variant="info">Under Review</Badge>;
      case "bill_created":
        return <Badge as="span" variant="success">Approved</Badge>;
      case "received":
        return <Badge as="span" variant="info">Received</Badge>;
      case "matched":
        return <Badge as="span" variant="info">Matched</Badge>;
      case "review_needed":
        return <Badge as="span" variant="warning">Under Review</Badge>;
      case "duplicate_ignored":
        return <Badge as="span" variant="ltgrey">Duplicate</Badge>;
      case "revision_pending":
        return <Badge as="span" variant="warning">Revision Under Review</Badge>;
      case "superseded":
        return <Badge as="span" variant="ltgrey">Superseded</Badge>;
      case "rejected":
        return <Badge as="span" variant="danger">Rejected</Badge>;
      default:
        return <Badge as="span" variant="ltgrey">{status}</Badge>;
    }
  };

  const renderVariance = (row: any) => {
    if (row.variancePct == null) return null;
    const pct = Number(row.variancePct);
    const amt = Number(row.varianceAmount);
    if (amt === 0) return null;
    const color = Math.abs(pct) > 5 ? themevals.colors.danger : themevals.colors.dkgrey;
    const sign = amt >= 0 ? "+" : "";
    return (
      <span style={{ color, fontSize: "12px" }}>
        {sign}${amt.toFixed(2)} ({sign}{pct.toFixed(1)}%)
      </span>
    );
  };

  /** Render a small 'AI' tag next to OCR-extracted values */
  const AiTag = ({ confidence }: { confidence?: string }) => (
    <span
      title={`AI-extracted (${confidence || "unknown"} confidence) \u2014 please verify`}
      style={{
        display: "inline-block",
        fontSize: "9px",
        fontWeight: 600,
        color: "#7C3AED",
        backgroundColor: "#EDE9FE",
        borderRadius: "3px",
        padding: "1px 4px",
        marginLeft: "6px",
        verticalAlign: "middle",
        lineHeight: "14px",
        letterSpacing: "0.5px",
      }}
    >
      AI
    </span>
  );

  const columns = [
    columnHelper.accessor((row: any) => row?.source, {
      id: "source",
      cell: (info: any) => {
        const src = info.getValue();
        const row = info.row.original;
        if (src === "email") {
          const verified = row.senderVerified !== false;
          return (
            <span
              title={row.senderEmail
                ? `Email from ${row.senderEmail}${!verified ? ' (unverified sender domain)' : ''}`
                : "Email submission"}
              style={{ fontSize: "12px", color: verified ? themevals.colors.dkgrey : '#D97706' }}
            >
              Email{!verified && " \u26A0"}
            </span>
          );
        }
        return <span style={{ fontSize: "12px", color: themevals.colors.dkgrey }}>Portal</span>;
      },
      header: () => <span>Source</span>,
      size: 55,
    }),
    columnHelper.accessor((row: any) => row?.invoiceNumber, {
      id: "invoiceNumber",
      cell: (info: any) => {
        const row = info.row.original;
        // Staff can enter OR correct the number on any invoice awaiting approval.
        if (isCompany && isPendingStatus(row.status)) {
          return (
            <Flex sx={{ alignItems: "center", gap: "4px" }}>
            <input
              type="text"
              placeholder="Enter invoice #"
              defaultValue={getEditedInvoiceNumber(row)}
              onBlur={(e) => updateEditedField(row.id, "invoiceNumber", e.target.value)}
              onChange={(e) => updateEditedField(row.id, "invoiceNumber", e.target.value)}
              style={{
                width: "110px",
                padding: "4px 6px",
                fontSize: "12px",
                border: `1px solid ${themevals.colors.inputOutline}`,
                borderRadius: "4px",
              }}
            />
            {row.extracted?.invoiceNumber && <AiTag confidence={row.extracted.confidence} />}
            </Flex>
          );
        }
        const invNum = info.getValue();
        return invNum ? (<span>{invNum}{isCompany && row.extracted?.invoiceNumber && <AiTag confidence={row.extracted.confidence} />}</span>) : "\u2014";
      },
      header: () => <span>Invoice #</span>,
    }),
    columnHelper.accessor((row: any) => row?.amount, {
      id: "amount",
      cell: (info: any) => {
        const val = info.getValue();
        const row = info.row.original;
        // Staff can enter OR correct the amount on any invoice awaiting approval.
        if (isCompany && isPendingStatus(row.status)) {
          return (
            <Box>
            <Flex sx={{ alignItems: "center", gap: "2px" }}>
              <span style={{ fontSize: "12px" }}>$</span>
              <input
                type="text"
                placeholder="0.00"
                defaultValue={getEditedAmount(row)}
                onBlur={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, "");
                  updateEditedField(row.id, "amount", v);
                }}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, "");
                  updateEditedField(row.id, "amount", v);
                }}
                style={{
                  width: "80px",
                  padding: "4px 6px",
                  fontSize: "12px",
                  border: `1px solid ${themevals.colors.inputOutline}`,
                  borderRadius: "4px",
                }}
              />
              {row.extracted?.amount != null && <AiTag confidence={row.extracted.confidence} />}
            </Flex>
            <Box>{renderVariance(row)}</Box>
            </Box>
          );
        }
        return (
          <Box>
            {val != null
              ? (<span>${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 })}{isCompany && row.extracted?.amount != null && <AiTag confidence={row.extracted.confidence} />}</span>)
              : "\u2014"}
            {isCompany && isPendingStatus(row.status) && (
              <Box>{renderVariance(row)}</Box>
            )}
          </Box>
        );
      },
      header: () => <span>Amount</span>,
    }),
    columnHelper.accessor((row: any) => row?.fileName, {
      id: "fileName",
      cell: (info: any) => {
        const row = info.row.original;
        const name = info.getValue() || "View File";
        return (
          <Box>
            {row.filePath ? (
              <Box
                sx={{ color: themevals.colors.primary, cursor: "pointer", textDecoration: "underline" }}
                onClick={() => openFilePreview(row)}
              >
                {name}
              </Box>
            ) : (
              <Box sx={{ color: themevals.colors.dkgrey }}>{name}</Box>
            )}
            {row.invoiceUrl && (
              <a
                href={row.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: "11px", color: themevals.colors.primary, display: "block", marginTop: "2px" }}
              >
                Invoice Link &#8599;
              </a>
            )}
          </Box>
        );
      },
      header: () => <span>File</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Submitted</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => renderStatusBadge(info.getValue()),
      header: () => <span>Status</span>,
    }),
    ...(isCompany
      ? [
          columnHelper.accessor((row: any) => row, {
            id: "actions",
            cell: (info: any) => {
              const row = info.getValue();
              if (!isPendingStatus(row.status)) return null;
              const isProcessing = processing === row.id;
              return (
                <Flex sx={{ gap: "8px" }}>
                  <BasicButton
                    scheme="primary"
                    onClick={() => handleApprove(row)}
                    disabled={isProcessing}
                    styles={{ padding: "4px 12px", fontSize: "12px" }}
                  >
                    {isProcessing ? "..." : "Approve"}
                  </BasicButton>
                  <BasicButton
                    scheme="secondaryOutline"
                    onClick={() => openRejectModal(row)}
                    disabled={isProcessing}
                    styles={{ padding: "4px 12px", fontSize: "12px" }}
                  >
                    Reject
                  </BasicButton>
                </Flex>
              );
            },
            header: () => <span>Actions</span>,
          }),
        ]
      : [
          columnHelper.accessor((row: any) => row?.uploadedBy, {
            id: "uploadedBy",
            cell: (info: any) => {
              const uploadedBy = info.getValue();
              return uploadedBy
                ? `${uploadedBy.firstName || ""} ${uploadedBy.lastName || ""}`.trim() || "—"
                : "—";
            },
            header: () => <span>Uploaded By</span>,
          }),
        ]),
  ];

  // Show rejection notes to provider if the latest invoice was rejected
  // Hide when PO is closed or paid — rejection is no longer actionable
  // PO is closed or fully paid — provider alerts are no longer actionable
  const poClosedOrPaid = Number(po.status ?? 0) === 5 || Number(po.paymentStatus ?? 0) === 2;

  const latestRejected =
    isProvider &&
    !poClosedOrPaid &&
    invoices &&
    invoices.length > 0 &&
    invoices[0]?.status === "rejected" &&
    invoices[0]?.notes;

  // Show approval alert to provider if an invoice was recently approved
  // Hide when PO is closed (status 5) or paid (paymentStatus 2) — no longer actionable
  const latestApproved =
    isProvider &&
    !poClosedOrPaid &&
    invoices &&
    invoices.find((inv: any) => inv.status === "bill_created");

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Invoices
      </H5>

      {latestRejected && (
        <Box
          sx={{
            backgroundColor: "#FFF3CD",
            border: "1px solid #FFEEBA",
            borderRadius: "6px",
            padding: "12px 16px",
            marginBottom: "12px",
          }}
        >
          <P fontSize="13px" fontWeight="600" marginBottom="4px">
            Your invoice was rejected
          </P>
          <P fontSize="13px" color={themevals.colors.dkgrey}>
            {latestRejected}
          </P>
        </Box>
      )}

      {latestApproved && !latestRejected && (
        <Box
          sx={{
            backgroundColor: "#DDE8D2",
            border: "1px solid #C5D6B4",
            borderRadius: "6px",
            padding: "12px 16px",
            marginBottom: "12px",
          }}
        >
          <P fontSize="13px" fontWeight="600" marginBottom="4px" color="#3D5A20">
            Your invoice has been approved
          </P>
          <P fontSize="13px" color="#3D5A20">
            Invoice {latestApproved.invoiceNumber || ""} for {"$"}{Number(latestApproved.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })} has been approved and a bill has been created.
          </P>
        </Box>
      )}

      {canSubmitInvoice && !hasBlockingInvoice && (
        <Flex sx={{ justifyContent: "flex-end" }}>
          <BasicButton scheme="secondaryOutline" onClick={openSubmitInvoice}>
            <PlusIcon fill="#000" />
            {invoices && invoices.some((inv: any) => inv.status === "bill_created")
              ? "Submit Revised Invoice"
              : invoices && invoices.length > 0
              ? "Resubmit Invoice"
              : "Submit Invoice"}
          </BasicButton>
        </Flex>
      )}

      <Flex sx={{ width: "100%", marginTop: "20px", flexDirection: "column" }}>
        {invoices === null ? (
          <Flex sx={{ justifyContent: "center", alignItems: "center", width: "100%" }}>
            <Spinner size={24} />
          </Flex>
        ) : invoices.length > 0 ? (
          <TableBasic
            data={invoices}
            columns={columns}
            enablePaging={false}
            enableSearch={false}
            customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
            noFirstColumnMinWidth={true}
            size="sm"
          />
        ) : (
          <Flex sx={{ justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
            <P fontFamily="Poppins" fontSize="14px" color="gray">
              {canSubmitInvoice
                ? "No invoices yet."
                : "No invoices yet. Invoice submission is available once the PO is accepted."}
            </P>
          </Flex>
        )}
      </Flex>

      {showSubmitModal && (
        <SubmitInvoice
          po={po}
          isOpen={showSubmitModal}
          toggle={() => setShowSubmitModal(false)}
          onSuccess={handleSubmitSuccess}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && rejectTarget && (
        <Modal isOpen={showRejectModal} toggle={() => setShowRejectModal(false)} size="sm" centered>
          <ModalHeader toggle={() => setShowRejectModal(false)}>
            Reject Invoice
          </ModalHeader>
          <ModalBody>
            <P marginBottom="10px">
              Rejecting invoice <strong>{rejectTarget.invoiceNumber || rejectTarget.id?.slice(0, 8)}</strong>.
            </P>
            <P fontSize="13px" color={themevals.colors.dkgrey} marginBottom="16px">
              The provider will see your rejection reason and can resubmit.
            </P>
            <TextInput
              label="Reason for rejection"
              name="rejectNotes"
              type="textarea"
              value={rejectNotes}
              onChange={(val: any) => setRejectNotes(val || "")}
              placeholder="e.g. Amount doesn't match PO, wrong PO number, missing line items..."
              rows={3}
            />
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondaryOutline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </BasicButton>
            <BasicButton
              scheme="danger"
              onClick={handleReject}
              disabled={processing === rejectTarget.id}
            >
              {processing === rejectTarget.id ? "Rejecting..." : "Reject Invoice"}
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}

      {/* Approve Confirmation Modal (zero/low variance) */}
      {showApproveModal && approveTarget && (
        <Modal isOpen={showApproveModal} toggle={() => { setShowApproveModal(false); setApproveTarget(null); }} size="sm" centered>
          <ModalHeader toggle={() => { setShowApproveModal(false); setApproveTarget(null); }}>
            Approve Invoice
          </ModalHeader>
          <ModalBody>
            <P marginBottom="10px">
              {/* What staff typed, not the stored value (was $0.00 for a hand-entered amount). */}
              Approve invoice <strong>{getEditedInvoiceNumber(approveTarget) || approveTarget.id?.slice(0, 8)}</strong> for{" "}
              <strong>${Number(getEditedAmount(approveTarget) || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>?
            </P>
            <P fontSize="13px" color={themevals.colors.dkgrey}>
              This will create a bill in Zoho Books and mark the PO as billed.
            </P>
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondaryOutline" onClick={() => { setShowApproveModal(false); setApproveTarget(null); }}>
              Cancel
            </BasicButton>
            <BasicButton
              scheme="primary"
              onClick={handleConfirmApprove}
              disabled={processing === approveTarget.id}
            >
              {processing === approveTarget.id ? "Approving..." : "Approve"}
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}

      {/* Variance Confirmation Modal */}
      {showVarianceModal && varianceTarget && (
        <Modal isOpen={showVarianceModal} toggle={() => { setShowVarianceModal(false); setVarianceTarget(null); }} size="md" centered>
          <ModalHeader toggle={() => { setShowVarianceModal(false); setVarianceTarget(null); }}>
            Approve with Variance
          </ModalHeader>
          <ModalBody>
            <Box sx={{
              backgroundColor: "#FFF3CD",
              border: "1px solid #FFEEBA",
              borderRadius: "6px",
              padding: "16px",
              marginBottom: "16px",
            }}>
              <P fontFamily="Poppins" fontSize="14px" fontWeight="600" marginBottom="8px" color="#856404">
                Invoice amount doesn&apos;t match PO total
              </P>
              <P fontSize="13px" color="#856404">
                This invoice is{" "}
                <strong>
                  ${Math.abs(Number(varianceTarget.varianceAmount || 0)).toFixed(2)}{" "}
                  {Number(varianceTarget.varianceAmount || 0) >= 0 ? "over" : "under"}
                </strong>
                {" "}the PO total ({Number(varianceTarget.variancePct || 0) >= 0 ? "+" : ""}
                {Number(varianceTarget.variancePct || 0).toFixed(1)}%).
              </P>
            </Box>
            <Flex sx={{ justifyContent: "space-between", marginBottom: "8px" }}>
              <P fontSize="13px" color={themevals.colors.dkgrey}>Invoice amount:</P>
              <P fontSize="13px" fontWeight="600">${Number(varianceTarget.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</P>
            </Flex>
            <Flex sx={{ justifyContent: "space-between", marginBottom: "16px" }}>
              <P fontSize="13px" color={themevals.colors.dkgrey}>Current PO total:</P>
              <P fontSize="13px" fontWeight="600">
                ${(Number(varianceTarget.amount || 0) - Number(varianceTarget.varianceAmount || 0)).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </P>
            </Flex>
            <P fontSize="13px" color={themevals.colors.dkgrey} marginBottom="8px">
              Approving will add a &ldquo;Change order adjustment&rdquo; line item to the PO for{" "}
              <strong>${Number(varianceTarget.varianceAmount || 0).toFixed(2)}</strong>,
              update the PO total to match the invoice amount, and create the Zoho bill.
            </P>
            <P fontSize="12px" color={themevals.colors.dkgrey} fontStyle="italic">
              This typically reflects a change order that hasn&apos;t been updated on the PO yet.
              If the invoice amount is wrong, reject it instead.
            </P>
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondaryOutline" onClick={() => { setShowVarianceModal(false); setVarianceTarget(null); }}>
              Cancel
            </BasicButton>
            <BasicButton
              scheme="danger"
              onClick={() => { setShowVarianceModal(false); openRejectModal(varianceTarget); }}
              styles={{ marginRight: "auto" }}
            >
              Reject Instead
            </BasicButton>
            <BasicButton
              scheme="primary"
              onClick={handleApproveWithVariance}
              disabled={processing === varianceTarget.id}
            >
              {processing === varianceTarget.id ? "Approving..." : "Approve & Update PO"}
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}
      {/* File Preview Modal */}
      {showFilePreview && (
        <Modal isOpen={showFilePreview} toggle={() => { setShowFilePreview(false); setFilePreviewUrl(null); }} size="lg" centered>
          <ModalHeader toggle={() => { setShowFilePreview(false); setFilePreviewUrl(null); }}>
            {filePreviewName}
          </ModalHeader>
          <ModalBody>
            {filePreviewLoading && (
              <Flex sx={{ justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <Spinner size={32} />
              </Flex>
            )}
            {!filePreviewLoading && filePreviewUrl && (
              isImageFile(filePreviewName) ? (
                <Flex sx={{ justifyContent: "center" }}>
                  <img
                    src={filePreviewUrl}
                    alt={filePreviewName}
                    style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "4px" }}
                  />
                </Flex>
              ) : (
                <iframe
                  src={filePreviewUrl}
                  title={filePreviewName}
                  style={{ width: "100%", height: "70vh", border: "none", borderRadius: "4px" }}
                />
              )
            )}
            {!filePreviewLoading && !filePreviewUrl && (
              <Flex sx={{ justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey}>Unable to load file preview.</P>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            {filePreviewUrl && (
              <BasicButton
                scheme="secondaryOutline"
                onClick={() => window.open(filePreviewUrl, "_blank")}
              >
                Open in New Tab
              </BasicButton>
            )}
            <BasicButton scheme="secondaryOutline" onClick={() => { setShowFilePreview(false); setFilePreviewUrl(null); }}>
              Close
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default InvoicesList;
